import React, { useState, useRef } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Download, Upload, ShieldAlert, Database, CheckCircle2, Loader2, FileArchive } from 'lucide-react';

/**
 * Sauvegarde / Restauration de la base de données.
 *
 * - Export : télécharge un fichier .json.gz de toutes les collections.
 * - Import : restaure un fichier de sauvegarde (mode fusion ou remplacement).
 *
 * Usage principal : migrer les données de la preview vers la production après
 * déploiement (la base de production démarre vierge).
 */
const AdminBackup = () => {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('merge');
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/backup/export', { responseType: 'blob' });
      const dispo = res.headers['content-disposition'] || '';
      const match = dispo.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `backup_${Date.now()}.json.gz`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sauvegarde téléchargée');
    } catch (e) {
      toast.error("Erreur lors de l'export de la sauvegarde");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Sélectionnez un fichier de sauvegarde');
      return;
    }
    if (mode === 'replace') {
      const ok = window.confirm(
        "⚠️ Mode REMPLACEMENT\n\n" +
        "Chaque collection présente dans la sauvegarde sera VIDÉE puis remplacée " +
        "par les données du fichier. Les données actuelles correspondantes seront perdues.\n\n" +
        "Confirmer la restauration en mode remplacement ?"
      );
      if (!ok) return;
    }
    setImporting(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', mode);
      const { data } = await api.post('/admin/backup/import', formData);
      setResult(data);
      toast.success(`Restauration terminée : ${data.total_restored} documents`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur lors de l'import";
      toast.error(detail);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="admin-backup-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold text-[#191D1E] flex items-center gap-2">
          <Database className="w-6 h-6 text-[#397DFF]" />
          Sauvegarde / Restauration
        </h1>
        <p className="text-[#687175] mt-1 text-sm">
          Exportez l'intégralité de vos données (rapports, comptes, agences, barèmes, grilles…)
          dans un fichier de sauvegarde, ou restaurez-les depuis un fichier.
        </p>
      </div>

      {/* Avertissement migration */}
      <div className="flex items-start gap-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="backup-info">
        <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
        <div>
          <p className="font-semibold">Migration preview → production</p>
          <p className="mt-1">
            La base de production démarre <strong>vierge</strong> après déploiement : les données de
            la preview ne sont pas transférées automatiquement. Pour les conserver, <strong>exportez</strong>
            {' '}depuis la preview, puis <strong>importez</strong> le fichier sur l'application déployée.
            Les mots de passe (hashés) sont préservés : vos utilisateurs gardent leurs identifiants.
          </p>
        </div>
      </div>

      {/* Export */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg p-6">
        <h2 className="font-semibold text-[#191D1E] flex items-center gap-2 mb-2">
          <Download className="w-5 h-5 text-[#10D183]" /> Exporter la sauvegarde
        </h2>
        <p className="text-[#687175] text-sm mb-4">
          Génère un fichier compressé <code className="text-xs bg-[#F5F6F9] px-1 py-0.5 rounded">.json.gz</code>{' '}
          contenant toutes les collections de la base actuelle.
        </p>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#10D183] hover:bg-[#0FB978] text-white rounded-lg font-medium disabled:opacity-60"
          data-testid="backup-export-btn"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {exporting ? 'Export en cours…' : 'Télécharger la sauvegarde'}
        </button>
      </div>

      {/* Import */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg p-6">
        <h2 className="font-semibold text-[#191D1E] flex items-center gap-2 mb-2">
          <Upload className="w-5 h-5 text-[#397DFF]" /> Restaurer depuis une sauvegarde
        </h2>
        <p className="text-[#687175] text-sm mb-4">
          Importez un fichier de sauvegarde <code className="text-xs bg-[#F5F6F9] px-1 py-0.5 rounded">.json.gz</code>{' '}
          (ou <code className="text-xs bg-[#F5F6F9] px-1 py-0.5 rounded">.json</code>) précédemment exporté.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".gz,.json,application/gzip,application/json"
          onChange={(e) => { setFile(e.target.files[0] || null); setResult(null); }}
          className="block w-full text-sm text-[#687175] mb-4 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#EEF2F6] file:text-[#397DFF] hover:file:bg-[#E0E9F2]"
          data-testid="backup-import-file"
        />
        {file && (
          <p className="text-xs text-[#687175] mb-4 flex items-center gap-1.5">
            <FileArchive className="w-4 h-4" /> {file.name} ({(file.size / 1024 / 1024).toFixed(2)} Mo)
          </p>
        )}

        <div className="mb-4 space-y-2">
          <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="backup-mode-merge">
            <input type="radio" name="mode" value="merge" checked={mode === 'merge'} onChange={() => setMode('merge')} className="mt-1" />
            <span>
              <span className="font-medium text-[#191D1E]">Fusionner</span> (recommandé) — ajoute/met à jour les documents par identifiant, sans supprimer l'existant.
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="backup-mode-replace">
            <input type="radio" name="mode" value="replace" checked={mode === 'replace'} onChange={() => setMode('replace')} className="mt-1" />
            <span>
              <span className="font-medium text-[#E53935]">Remplacer</span> — vide chaque collection de la sauvegarde puis réinsère. Restauration complète (destructif).
            </span>
          </label>
        </div>

        <button
          onClick={handleImport}
          disabled={importing || !file}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#397DFF] hover:bg-[#2E5BCC] text-white rounded-lg font-medium disabled:opacity-60"
          data-testid="backup-import-btn"
        >
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {importing ? 'Restauration en cours…' : 'Importer et restaurer'}
        </button>

        {result && (
          <div className="mt-5 border border-[#10D183]/40 bg-[#ECFDF5] rounded-lg p-4" data-testid="backup-import-result">
            <p className="font-semibold text-[#047857] flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5" />
              Restauration terminée — {result.total_restored} documents ({Object.keys(result.collections || {}).length} collections)
            </p>
            <div className="max-h-48 overflow-y-auto text-xs text-[#374151] grid grid-cols-2 gap-x-6 gap-y-1">
              {Object.entries(result.collections || {}).map(([col, n]) => (
                <div key={col} className="flex justify-between border-b border-[#D1FAE5] py-0.5">
                  <span className="font-mono">{col}</span>
                  <span className={typeof n === 'number' ? 'text-[#047857]' : 'text-[#E53935]'}>{String(n)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBackup;
